import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useRegisterOverlay } from '@/stores/overlayStore';
import {
  Trash2,
  Plus,
  Info,
  Check,
  Loader2,
  UserPlus,
  ChevronLeft,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { emailService, householdService, childService } from '@/services';
import { useHouseholdStore } from '@/stores/householdStore';
import {
  QuotaExceededError,
  type ProposalTarget,
} from '@/services/email.service';
import { resolveProposal } from '@/lib/reviewProposal';
import { parseRecurrence, withUntil } from '@/lib/recurrence';
import { PROPOSAL_COPY } from './proposalCopy';
import { quotaExceededMessage } from '@/constants/billing';
import { formatReceivedTimestamp } from '@/lib/date';
import { toIsoWithTz } from '@/lib/tz';
import { findFamilyChildCandidates } from '@/lib/familyChild';
import { usePermissions } from '@/hooks';
import { useUserStore } from '@/stores/userStore';
import type {
  Email,
  EmailExtraction,
  ReviewItem,
  ReviewItemWithChild,
  Child,
} from '@/types';
import { Button } from '@/components/ui';
import { captureEvent } from '@/lib/analytics';
import { reviewConfirmGate } from '@/lib/review-items';
import { ConfirmWithoutDateModal } from '@/components/domain/shared/ConfirmWithoutDateModal';
import { DiscardEmailModal } from '@/components/domain/shared/DiscardEmailModal';
import { SmartSummaryBlock } from '@/components/domain/shared/SmartSummaryBlock';
import { SourceInfo } from '@/components/domain/shared/SourceInfo';
import { LoadingSpinner } from '@/components/states/LoadingSpinner';
import { ItemFormModal, type AvailableMember } from './ItemFormModal';
import { readForwardProvenance, senderContextLabel } from '@/lib/sender';
import {
  EventGroupCard,
  TaskCard,
  SourceReferenceSection,
  getAttachments,
  type EventGroup,
  type GroupedItems,
  type ItemMemberAssignments,
  type ReviewCardProposals,
} from './_components';

type ActionState = 'idle' | 'success' | 'discard-success';

/**
 * The plan-limit banner shown when `emails-approve` refuses with a 403
 * QUOTA_EXCEEDED.
 *
 * Its *placement* is the whole point (YAR-393). This markup used to sit
 * inline in the scroll body, ~120 lines of DOM above the Confirm CTA, which
 * lives in a fixed footer. On a phone that meant the user tapped Confirm,
 * the request 403'd, and the explanation rendered off-screen — Confirm read
 * as a dead button, with no reason given and no route to paying. Nothing
 * scrolled or focused it, so the message effectively did not exist.
 *
 * It is now rendered by the footer, immediately above the button that
 * produced the error, and `role="alert"` announces it to assistive tech,
 * which previously got nothing at all.
 *
 * Copy and behaviour are unchanged — only where it lands.
 */
function QuotaLimitBanner({
  error,
  canUpgrade,
  onUpgrade,
}: {
  error: QuotaExceededError;
  canUpgrade: boolean;
  onUpgrade: () => void;
}) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-danger p-4 text-center flex flex-col items-center gap-3"
    >
      <p className="text-danger text-sm m-0 font-medium">
        Limit reached, upgrade to continue
      </p>
      <p className="text-muted-foreground text-sm m-0">
        {quotaExceededMessage(error.tier, error.used, error.quota)}
      </p>
      {canUpgrade && (
        <Button
          variant="primary"
          size="sm"
          onClick={onUpgrade}
        >
          Upgrade plan
        </Button>
      )}
    </div>
  );
}

const EMPTY_REVIEW_DATA = {
  items: [] as ReviewItemWithChild[],
  targets: new Map<string, ProposalTarget>(),
  targetsFailed: false,
};

/** YAR-140 AC3, verbatim: unlike a couldn't-read card, this email has items. */
const REVIEW_ITEMS_DISCARD_BODY =
  "The items won't be added to your schedule. You can always find the original in Gmail under the 'Yaruta' label.";

export interface EmailReviewModalProps {
  emailId: string;
  open: boolean;
  onClose: () => void;
  /** Called after the user successfully approves the email. */
  onApproved?: () => void;
  /** Called after the user discards the email. */
  onDismissed?: () => void;
}

export function EmailReviewModal({
  emailId,
  open,
  onClose,
  onApproved,
  onDismissed,
}: EmailReviewModalProps) {
  // Full-screen while open: hide and pause the bottom toast slot (YAR-476).
  useRegisterOverlay(open);
  // Reviewing (approve/dismiss/edit AI extractions) is a manager capability
  // (Admin + Co-Admin); RLS + the review_items policy are the real gate.
  const { canReview } = usePermissions();
  const userTimezone = useUserStore((s) => s.timezone);
  const navigate = useNavigate();
  const [email, setEmail] = useState<Email | null>(null);
  const [extraction, setExtraction] = useState<EmailExtraction | null>(null);
  const [reviewItems, setReviewItems] = useState<ReviewItemWithChild[]>([]);
  // Schedule items that hinted review items propose to update / cancel
  // (YAR-493), keyed by the target's id. `targetsFailed` = the read failed,
  // so a hinted card must not fall back to looking like a new item.
  const [proposalTargets, setProposalTargets] = useState<
    Map<string, ProposalTarget>
  >(() => new Map());
  const [proposalTargetsFailed, setProposalTargetsFailed] = useState(false);
  const [keepingAsNewId, setKeepingAsNewId] = useState<string | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  // Lowercased AI mention → canonical family child name picked by the
  // user when the matcher returned more than one candidate. Carried
  // alongside review_items so the underlying rows stay untouched
  // (so we don't accidentally clobber the AI's verbatim extraction).
  const [disambiguation, setDisambiguation] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [childSuggestionDismissed, setChildSuggestionDismissed] =
    useState(false);
  const [creatingChild, setCreatingChild] = useState(false);
  // Household roster used to render member avatars on cards and to
  // populate the Members dropdown inside ItemFormModal. Loaded once
  // per email open; unchanged unless the user switches household.
  const [availableMembers, setAvailableMembers] = useState<AvailableMember[]>(
    [],
  );
  // Per-review-item member picks. Source of truth lives in this modal
  // while the user iterates — review_items has no member column, so the
  // map is what feeds both the right-side AvatarGroup and the
  // `item_member_ids` payload at approve time. Cleared on close.
  const [itemMemberIds, setItemMemberIds] = useState<ItemMemberAssignments>(
    () => new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionState, setActionState] = useState<ActionState>('idle');
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  // Path B: open when the user taps Confirm while undated tasks remain.
  const [confirmWithoutDateOpen, setConfirmWithoutDateOpen] = useState(false);
  const [sourceExpanded, setSourceExpanded] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [formModal, setFormModal] = useState<{
    mode: 'create' | 'edit';
    initialType?: 'task' | 'event';
    editItem?: ReviewItem | null;
    presetRelatedEventId?: string | null;
    presetRelatedEventTitle?: string;
  } | null>(null);

  const initialExpansionDone = useRef(false);

  const fetchData = useCallback(async () => {
    if (!emailId) return;
    initialExpansionDone.current = false;
    setLoading(true);
    setError(null);

    try {
      // The active household is already resolved and in the store — the
      // shell can't have mounted without it. Re-deriving it here cost the
      // full auth.getUser -> users -> household_members waterfall. In the
      // warm path this line does not await at all.
      const hid =
        useHouseholdStore.getState().householdId ??
        (await emailService.getHouseholdIdForCurrentUser().catch(() => null));
      if (hid) setHouseholdId(hid);

      // All five reads are independent once emailId and hid are known, and
      // used to run as THREE serial stages. They are created INSIDE
      // Promise.all so every one of them has a rejection handler attached
      // in the same tick: holding a bare promise across an await (as an
      // earlier version of this did with getEmail) lets a rejection land
      // with no handler, which the browser reports as an unhandled
      // rejection — a phantom error in Sentry on top of the real one.
      //
      // getEmail still publishes from its own .then, so the message body
      // paints as soon as it lands instead of waiting for the slowest read.
      const [, extractionData, reviewData, familyChildren, members] =
        await Promise.all([
          emailService.getEmail(emailId).then((data) => {
            setEmail(data);
            return data;
          }),
          emailService.getExtraction(emailId).catch(() => null),
          hid
            ? emailService
                .getReviewItemsWithTargets(emailId, hid)
                .catch(() => EMPTY_REVIEW_DATA)
            : Promise.resolve(EMPTY_REVIEW_DATA),
          hid ? householdService.getChildren(hid) : Promise.resolve([]),
          hid
            ? householdService.getMembers(hid).catch(() => [])
            : Promise.resolve([]),
        ]);

      setExtraction(extractionData);
      setReviewItems(reviewData.items);
      setProposalTargets(reviewData.targets);
      setProposalTargetsFailed(reviewData.targetsFailed);
      setChildren(familyChildren);
      // Flatten HouseholdMemberWithUser → AvailableMember (drops the
      // row when the joined users record is missing; Pick<users…>
      // can be null on partially-provisioned rows).
      setAvailableMembers(
        members
          .filter((m) => m.users)
          .map((m) => ({
            user_id: m.user_id,
            display_name: m.users!.display_name ?? m.users!.email ?? '',
            email: m.users!.email,
            avatar_emoji: m.users!.avatar_emoji,
            avatar_color: m.users!.avatar_color,
            avatar_image_path: m.users!.avatar_image_path,
          })),
      );
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load email'));
    } finally {
      setLoading(false);
    }
  }, [emailId]);

  // Reset state whenever the modal opens with a new emailId
  useEffect(() => {
    if (open && emailId) {
      captureEvent('email_review_opened', {
        source: 'review_modal',
        status: 'opened',
      });
      setActionState('idle');
      setError(null);
      setDiscardModalOpen(false);
      setConfirmWithoutDateOpen(false);
      setSourceExpanded(false);
      setExpandedEvents(new Set());
      setFormModal(null);
      setChildSuggestionDismissed(false);
      setSelectedChildId(null);
      setDisambiguation(new Map());
      setItemMemberIds(new Map());
      fetchData();
    }
  }, [open, emailId, fetchData]);

  // Auto-close after success/discard. Reset actionState back to 'idle'
  // before the close callbacks fire so the effect cannot re-arm a fresh
  // 1.5s timer on the next parent re-render. Without this, callers that
  // pass inline onClose / onApproved / onDismissed (e.g. Inbox.tsx) get
  // a feedback loop: each re-render produces new callback refs, the
  // effect cleanup+re-run schedules another timer, the timer fires
  // onDismissed (refetch), refetch causes another re-render, and so on
  // every ~2s.
  useEffect(() => {
    if (actionState !== 'success' && actionState !== 'discard-success') return;
    const finalState = actionState;
    const timer = setTimeout(() => {
      setActionState('idle');
      if (finalState === 'success') onApproved?.();
      else onDismissed?.();
      onClose();
    }, 1500);
    return () => clearTimeout(timer);
  }, [actionState, onApproved, onDismissed, onClose]);

  // The query in `getReviewItems` already drops dismissed rows, but we
  // re-filter here as a defensive layer — if the data layer ever ships a
  // dismissed row (e.g., a stale cached realtime payload), every consumer
  // in this component must agree on what counts as "visible". Hoisting
  // the filter into its own memo keeps `totalItemCount`, `groupedItems`,
  // and `childSuggestion` from drifting (e.g., the footer button counting
  // dismissed items the body never renders).
  const visibleItems = useMemo(
    () => reviewItems.filter((i) => i.status !== 'dismissed'),
    [reviewItems],
  );

  // Group items: events with their related tasks, standalone tasks.
  const groupedItems = useMemo((): GroupedItems => {
    const events = visibleItems.filter((i) => i.type === 'event');
    const tasks = visibleItems.filter((i) => i.type === 'task');

    const eventGroups: EventGroup[] = events.map((event) => ({
      event,
      relatedTasks: tasks.filter((t) => t.related_event_id === event.id),
    }));

    const linkedTaskIds = new Set(
      eventGroups.flatMap((g) => g.relatedTasks.map((t) => t.id)),
    );
    const standaloneTasks = tasks.filter((t) => !linkedTaskIds.has(t.id));

    return { eventGroups, standaloneTasks };
  }, [visibleItems]);

  // Date-completeness gate for the Confirm CTA (see lib/review-items.ts):
  // - 'blocked-events' → undated events disable the CTA (Path C)
  // - 'needs-task-confirmation' → undated tasks open a confirm modal (Path B)
  // - 'ready' → everything dated, confirm directly (Path A)
  const confirmGate = useMemo(
    () => reviewConfirmGate(visibleItems),
    [visibleItems],
  );

  // Auto-expand all events on first load only
  useEffect(() => {
    if (!initialExpansionDone.current && groupedItems.eventGroups.length > 0) {
      initialExpansionDone.current = true;
      setExpandedEvents(
        new Set(groupedItems.eventGroups.map((g) => g.event.id)),
      );
    }
  }, [groupedItems.eventGroups]);

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  // Derive child suggestion from the visible items only — a dismissed
  // item resurrected by a stale realtime payload must not surface the
  // "Add child" banner for a child the user never approved.
  const childSuggestion = useMemo(() => {
    const names = new Set<string>();
    for (const item of visibleItems) {
      for (const n of item.child_names) if (n) names.add(n);
      if (item.child_name) names.add(item.child_name);
    }
    if (names.size === 0) return null;

    const mentionedName = [...names][0];

    // If the user already disambiguated this mention via the picker,
    // honor the pick before re-running the matcher.
    const overrideName = disambiguation.get(mentionedName.toLowerCase());
    if (overrideName) {
      const overrideChild = children.find((c) => c.name === overrideName);
      if (overrideChild) {
        return {
          name: overrideChild.name,
          mode: 'matched' as const,
          existingChild: overrideChild,
        };
      }
      // Stale override (child deleted in another tab) — fall through.
    }

    // First-token-aware matcher so "John" binds to "John Smith"
    // without leaving an unmatched placeholder.
    const candidates = findFamilyChildCandidates(mentionedName, children);
    if (candidates.length === 1) {
      return {
        // Canonical family name so the indicator + badges agree.
        name: candidates[0].name,
        mode: 'matched' as const,
        existingChild: candidates[0],
      };
    }
    if (candidates.length > 1) {
      // Multiple siblings could match — let the user pick rather than
      // silently guessing and binding items to the wrong child.
      return {
        name: mentionedName,
        mode: 'ambiguous' as const,
        candidates,
      };
    }
    return { name: mentionedName, mode: 'new' as const };
  }, [visibleItems, children, disambiguation]);

  const matchedChildId =
    childSuggestion?.mode === 'matched'
      ? childSuggestion.existingChild.id
      : null;
  useEffect(() => {
    if (matchedChildId) {
      setSelectedChildId(matchedChildId);
    }
  }, [matchedChildId]);

  const handleDisambiguate = (mention: string, picked: Child) => {
    setDisambiguation((prev) => {
      const next = new Map(prev);
      next.set(mention.trim().toLowerCase(), picked.name);
      return next;
    });
    setSelectedChildId(picked.id);
  };

  const handleCreateChild = async () => {
    if (!householdId || !childSuggestion?.name) return;
    setCreatingChild(true);
    setError(null);
    try {
      const newChild = await childService.createChild(householdId, {
        name: childSuggestion.name,
      });
      setChildren((prev) => [...prev, newChild]);
      setSelectedChildId(newChild.id);
      setChildSuggestionDismissed(true);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to create child'),
      );
    } finally {
      setCreatingChild(false);
    }
  };

  const handleApprove = async () => {
    // Guard on actionLoading too (not just actionState): actionState stays
    // 'idle' for the whole in-flight window, so without this a second call
    // mid-request would not be blocked at the function level.
    if (
      !emailId ||
      !reviewItems.length ||
      actionState !== 'idle' ||
      actionLoading
    )
      return;
    setActionLoading(true);

    try {
      // Materialize the member map → Record<reviewItemId, string[]>
      // only when at least one item has assignments. The edge function
      // tolerates an empty/missing object, but skipping the field on
      // the wire keeps the legacy approve-without-members path
      // byte-identical for tests + diff review.
      const itemMembers: Record<string, string[]> = {};
      itemMemberIds.forEach((ids, id) => {
        if (ids.length > 0) itemMembers[id] = ids;
      });
      await emailService.approveEmail(emailId, {
        child_id: selectedChildId ?? undefined,
        item_member_ids:
          Object.keys(itemMembers).length > 0 ? itemMembers : undefined,
      });
      for (const item of visibleItems) {
        captureEvent('review_item_approved', { item_type: item.type });
      }
      await fetchData();
      setActionState('success');
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to approve email'),
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Leave the review screen for the plan page. Closing first keeps the
  // modal from sitting over /settings/plan after the route changes.
  const handleUpgradeClick = () => {
    onClose();
    navigate('/settings/plan');
  };

  // Confirm CTA click. Undated tasks (Path B) route through a confirmation
  // modal before committing; everything else approves directly. Undated
  // events (Path C) never reach here — the CTA is disabled in that state.
  const handleConfirmClick = () => {
    if (confirmGate.kind === 'needs-task-confirmation') {
      setConfirmWithoutDateOpen(true);
    } else {
      handleApprove();
    }
  };

  const handleDismiss = async () => {
    if (!emailId) return;
    setActionLoading(true);
    setDiscardModalOpen(false);

    try {
      await emailService.dismissEmail(emailId);
      for (const item of visibleItems) {
        captureEvent('review_item_dismissed', { item_type: item.type });
      }
      setActionState('discard-success');
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to dismiss email'),
      );
    } finally {
      setActionLoading(false);
    }
  };

  // "Keep as new" (YAR-493): the parent overrides a proposed update, so the
  // item is committed as a brand-new schedule item. The hint is cleared on
  // that one row; the row itself is never dropped. Patched in place rather
  // than refetched so the list doesn't flash a spinner.
  const handleKeepAsNew = async (item: ReviewItemWithChild) => {
    if (!householdId || keepingAsNewId) return;
    setKeepingAsNewId(item.id);
    try {
      const updated = await emailService.keepReviewItemAsNew(
        item.id,
        householdId,
      );
      setReviewItems((prev) =>
        prev.map((i) => (i.id === updated.id ? updated : i)),
      );
    } catch {
      setError(new Error(PROPOSAL_COPY.keepAsNewError));
    } finally {
      setKeepingAsNewId(null);
    }
  };

  // Recurring events (YAR-443): the end date is the only editable part of the
  // rule. Written through the ordinary review-item update and patched in
  // place, like "Keep as new". Rejects on failure so the card can say so
  // inline; the item itself is untouched.
  const handleChangeRecurrenceUntil = async (
    item: ReviewItemWithChild,
    until: string | null,
  ) => {
    if (!householdId) throw new Error('No active household');
    const rule = parseRecurrence(item.recurrence);
    if (!rule) return;
    const updated = await emailService.updateReviewItem(item.id, householdId, {
      recurrence: withUntil(rule, until),
    });
    setReviewItems((prev) =>
      prev.map((i) => (i.id === updated.id ? updated : i)),
    );
  };

  const handleRetry = async () => {
    if (!emailId || !householdId) return;
    setActionLoading(true);
    try {
      await emailService.retryEmail(emailId, householdId);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to retry email'));
    } finally {
      setActionLoading(false);
    }
  };

  // Don't render until open — saves a lot of work on initial mount
  if (!open) return null;

  const attachments = email ? getAttachments(email) : [];
  const attachmentCount = attachments.length;
  // The "View Source Reference" section is always visible whenever an email is
  // loaded — it's how users confirm where the extracted items came from. The
  // expand/collapse only controls whether the body/attachments panel renders.
  const hasSourceContent = !!email;
  // Read from visibleItems so the "Add N items" footer button stays in
  // sync with what the modal body actually renders, even when the data
  // layer ships a stray dismissed row.
  const totalItemCount = visibleItems.length;
  const proposals: ReviewCardProposals = {
    resolve: (item) =>
      resolveProposal(item, proposalTargets, proposalTargetsFailed),
    onKeepAsNew:
      email?.status !== 'PROCESSED' && canReview ? handleKeepAsNew : undefined,
    keepingAsNewId,
  };
  // VIEWER members get a read-only Review modal: no discard, no add,
  // no edit. They're viewing someone else's household; only PRIMARY can
  // commit AI-extracted items into the schedule.
  const showDiscardButton = !!email && actionState === 'idle' && canReview;
  // The Confirm CTA footer renders only for the PRIMARY with committable
  // items. When shown it is the modal's bottom-most element and owns the
  // home-indicator inset; the scroll body then keeps a flat 20px so the two
  // don't stack into an oversized gap on notched devices (mirrors Modal.tsx).
  const showFooterCta =
    totalItemCount > 0 &&
    actionState === 'idle' &&
    email?.status !== 'PROCESSED' &&
    canReview;
  // A plan-limit 403 is the one error that has to be read next to the button
  // that caused it, so the footer renders it rather than the scroll body
  // (YAR-393). Every state that can produce it also satisfies showFooterCta
  // — it only comes from handleApprove, which needs committable items and an
  // idle actionState — but the body keeps a fallback so the message can
  // never disappear entirely if that stops holding.
  const quotaError = error instanceof QuotaExceededError ? error : null;

  // ── Body content ──
  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="flex-1 flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  } else if (error && !email) {
    body = (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 py-20">
        <Info
          size={32}
          className="text-danger"
        />
        <p className="text-xl font-medium text-foreground">
          Unable to load email.
        </p>
        <Button
          variant="outline"
          onClick={fetchData}
        >
          Reload
        </Button>
      </div>
    );
  } else if (actionState === 'success') {
    body = (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 py-20">
        <Check
          size={32}
          className="text-foreground"
        />
        <p className="text-xl font-medium text-foreground">
          All items confirmed!
        </p>
      </div>
    );
  } else if (actionState === 'discard-success') {
    body = (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 py-20">
        <Check
          size={32}
          className="text-foreground"
        />
        <p className="text-xl font-medium text-foreground">Email discarded.</p>
      </div>
    );
  } else if (email) {
    body = (
      <div
        className={[
          'flex-1 overflow-y-auto px-5 pt-5 flex flex-col gap-5',
          // Footer (when shown) owns the home-indicator inset; only reserve
          // it on the body when the footer is absent so it's the last element.
          showFooterCta
            ? 'pb-5'
            : 'pb-[max(1.25rem,env(safe-area-inset-bottom))]',
        ].join(' ')}
      >
        {/* Email Title Card */}
        <div className="rounded-xl border border-border p-5">
          <div className="flex items-start justify-between gap-5">
            <h2 className="text-2xl font-bold text-foreground flex-1">
              {email.subject || '(no subject)'}
            </h2>
            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap pt-1">
              {formatReceivedTimestamp(email.created_at)}
            </span>
          </div>

          {extraction?.summary && (
            <SmartSummaryBlock
              summary={extraction.summary}
              className="mt-3"
            />
          )}

          <SourceInfo
            sender={email.from_email}
            {...readForwardProvenance(email.preprocessing_metadata)}
            senderOrg={senderContextLabel(email)}
            provider="Gmail"
            attachmentCount={attachmentCount}
            className="mt-3"
          />
        </div>

        {/* Review Items Group */}
        {(groupedItems.eventGroups.length > 0 ||
          groupedItems.standaloneTasks.length > 0) && (
          <div className="py-5 flex flex-col gap-4">
            {groupedItems.eventGroups.map((group) => (
              <EventGroupCard
                key={group.event.id}
                group={group}
                timezone={userTimezone}
                isExpanded={expandedEvents.has(group.event.id)}
                onToggle={() => toggleEventExpanded(group.event.id)}
                onEditItem={
                  canReview
                    ? (item) => {
                        if (!householdId) return;
                        setFormModal({ mode: 'edit', editItem: item });
                      }
                    : undefined
                }
                onAddTaskToEvent={
                  canReview
                    ? (evt) => {
                        if (!householdId) return;
                        setFormModal({
                          mode: 'create',
                          initialType: 'task',
                          presetRelatedEventId: evt.id,
                          presetRelatedEventTitle: evt.title,
                        });
                      }
                    : undefined
                }
                childrenData={children}
                disambiguation={disambiguation}
                readOnly={email?.status === 'PROCESSED' || !canReview}
                memberIdsByItem={itemMemberIds}
                availableMembers={availableMembers}
                proposals={proposals}
                onChangeRecurrenceUntil={
                  canReview ? handleChangeRecurrenceUntil : undefined
                }
              />
            ))}

            {groupedItems.standaloneTasks.map((task) => {
              const relEvtTitle = task.related_event_id
                ? groupedItems.eventGroups.find(
                    (g) => g.event.id === task.related_event_id,
                  )?.event.title
                : undefined;
              return (
                <TaskCard
                  key={task.id}
                  item={task}
                  timezone={userTimezone}
                  onEditItem={
                    canReview
                      ? (item) => {
                          if (!householdId) return;
                          setFormModal({ mode: 'edit', editItem: item });
                        }
                      : undefined
                  }
                  childrenData={children}
                  disambiguation={disambiguation}
                  relatedEventTitle={relEvtTitle}
                  memberIdsByItem={itemMemberIds}
                  availableMembers={availableMembers}
                  proposals={proposals}
                />
              );
            })}
          </div>
        )}

        {/* Child Suggestion Banner — "new" (not in family yet) */}
        {childSuggestion &&
          childSuggestion.mode === 'new' &&
          !childSuggestionDismissed && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <UserPlus
                  size={20}
                  className="text-primary flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground m-0">
                    &quot;{childSuggestion.name}&quot; mentioned in this email
                  </p>
                  <p className="text-xs text-muted-foreground m-0 mt-1">
                    This child isn&apos;t in your family yet. Would you like to
                    add them?
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreateChild}
                  loading={creatingChild}
                  disabled={creatingChild}
                >
                  Add {childSuggestion.name}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setChildSuggestionDismissed(true)}
                >
                  Skip
                </Button>
              </div>
            </div>
          )}

        {/* Child Suggestion Banner — ambiguous (multiple candidates) */}
        {childSuggestion &&
          childSuggestion.mode === 'ambiguous' &&
          !childSuggestionDismissed && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <UserPlus
                  size={20}
                  className="text-primary flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground m-0">
                    Which {childSuggestion.name} did this email mean?
                  </p>
                  <p className="text-xs text-muted-foreground m-0 mt-1">
                    Pick one to assign these items, or skip to leave them
                    unassigned for now.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {childSuggestion.candidates.map((candidate) => (
                  <Button
                    key={candidate.id}
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      handleDisambiguate(childSuggestion.name, candidate)
                    }
                  >
                    {candidate.name}
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setChildSuggestionDismissed(true)}
                >
                  Skip
                </Button>
              </div>
            </div>
          )}

        {/* Matched child indicator */}
        {childSuggestion &&
          childSuggestion.mode === 'matched' &&
          selectedChildId && (
            <div className="rounded-xl border border-border bg-surface-secondary p-4 flex items-center gap-3">
              <Check
                size={16}
                className="text-primary flex-shrink-0"
              />
              <p className="text-sm text-foreground m-0">
                Items will be assigned to{' '}
                <span className="font-semibold">{childSuggestion.name}</span>
              </p>
            </div>
          )}

        {/* Add Item placeholder — hidden once email is approved or for
            non-PRIMARY members (they can't mutate the household). */}
        {email?.status !== 'PROCESSED' && canReview && (
          <button
            type="button"
            className="w-full rounded-xl border border-dashed border-border-strong py-5 flex items-center justify-center gap-1.5 bg-transparent cursor-pointer"
            aria-label="Add item"
            onClick={() => {
              if (!householdId) return;
              setFormModal({ mode: 'create', initialType: 'task' });
            }}
          >
            <span className="text-sm font-semibold text-muted-foreground">
              Add item
            </span>
            <Plus
              size={16}
              className="text-muted-foreground"
            />
          </button>
        )}

        {/* View Source Reference */}
        {hasSourceContent && (
          <SourceReferenceSection
            email={email}
            attachments={attachments}
            attachmentCount={attachmentCount}
            expanded={sourceExpanded}
            onToggle={() => setSourceExpanded(!sourceExpanded)}
          />
        )}

        {/* Plan-limit fallback — normally the footer owns this banner, so
            this only renders in the state where there is no footer CTA to
            attach it to. See the `quotaError` note above. */}
        {quotaError && email && !showFooterCta && (
          <QuotaLimitBanner
            error={quotaError}
            canUpgrade={canReview}
            onUpgrade={handleUpgradeClick}
          />
        )}

        {/* Action error */}
        {error && email && !quotaError && (
          <div className="rounded-xl border border-danger p-4 text-center flex flex-col items-center gap-3">
            <p className="text-danger text-sm m-0">{error.message}</p>
            {(email.status === 'ERROR' || email.status === 'PROCESSING') && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                disabled={actionLoading}
                loading={actionLoading}
              >
                Try again
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Render ──
  return createPortal(
    <>
      {/* Backdrop + positioning */}
      <div
        className="ph-no-capture fixed inset-0 z-[200] flex items-end justify-center"
        role="dialog"
        aria-modal="true"
        aria-label="Review email"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-[5px]" />

        {/* Content card — the full viewport (YAR-302 sibling YAR-286).
            This screen is reached only from the Review tab, and there it is a
            destination, not a sheet: it used to stop 24px short of the top,
            leaving a strip of backdrop and reading as a partially-open modal.
            The rounded top cap goes with it — rounded corners flush against
            the screen edge look like a rendering fault, not a sheet.
            Schedule's item editor is a different component (`ItemFormModal`)
            and deliberately keeps its sheet treatment. */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="ph-no-capture relative bg-card text-card-foreground shadow-[0px_0px_16px_-4px_rgba(56,64,74,0.1)] border-x border-border w-full max-w-2xl h-dvh flex flex-col"
        >
          {/* Header */}
          <header className="flex items-center px-4 py-3 border-b border-border shrink-0">
            <div className="flex-1 flex items-start">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-md p-1 text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
                aria-label="Close review"
              >
                <ChevronLeft size={24} />
              </button>
            </div>
            {/* text/content/heading (#1a1a1a), not body (#2f2f2f) — YAR-131. */}
            <h1 className="text-xl font-bold text-text-heading">
              Review Items
            </h1>
            <div className="flex-1 flex justify-end">
              {showDiscardButton && (
                <button
                  type="button"
                  onClick={() => setDiscardModalOpen(true)}
                  // icon/default (#5e5e5e), not body (#2f2f2f) — YAR-131.
                  className="inline-flex items-center justify-center rounded-md p-1 text-icon-default hover:bg-muted transition-colors duration-150 cursor-pointer"
                  aria-label="Discard email"
                >
                  <Trash2 size={24} />
                </button>
              )}
            </div>
          </header>

          {body}

          {/* Footer CTA — only PRIMARY can commit items into the schedule. */}
          {showFooterCta && (
            <div className="border-t border-border px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] shrink-0 flex flex-col gap-2">
              {/* Plan limit: rendered here, not in the scroll body, so the
                  reason and the way out are both in view the moment Confirm
                  fails (YAR-393). */}
              {quotaError && (
                <QuotaLimitBanner
                  error={quotaError}
                  canUpgrade={canReview}
                  onUpgrade={handleUpgradeClick}
                />
              )}
              {confirmGate.kind === 'blocked-events' && (
                <p
                  className="text-sm text-warning text-center"
                  role="status"
                >
                  {confirmGate.events.length === 1
                    ? '1 event needs a date before you can confirm.'
                    : `${confirmGate.events.length} events need a date before you can confirm.`}
                </p>
              )}
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={
                  actionLoading ||
                  actionState !== 'idle' ||
                  totalItemCount === 0 ||
                  confirmGate.kind === 'blocked-events'
                }
                title={
                  confirmGate.kind === 'blocked-events'
                    ? 'Add a date to each event before you can confirm'
                    : undefined
                }
                className={[
                  'w-full flex items-center justify-center gap-1.5 bg-primary text-primary-foreground rounded-md py-2 px-4',
                  'text-base font-semibold cursor-pointer border-none',
                  'disabled:bg-disabled-bg disabled:text-disabled-text disabled:cursor-not-allowed',
                ].join(' ')}
              >
                {actionLoading ? (
                  <Loader2
                    size={16}
                    className="animate-spin"
                  />
                ) : (
                  `Confirm ${totalItemCount} Item${totalItemCount === 1 ? '' : 's'}`
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Item Form Modal (nested) */}
      {formModal && (
        <ItemFormModal
          mode={formModal.mode}
          // Review items are staged rows with a mutable `type` column, so the
          // Task/Event toggle stays editable when editing here (YAR-272).
          allowTypeChange
          initialType={formModal.initialType}
          initialData={formModal.editItem ?? null}
          presetRelatedEventId={formModal.presetRelatedEventId ?? null}
          presetRelatedEventTitle={formModal.presetRelatedEventTitle}
          availableEvents={groupedItems.eventGroups.map((g) => ({
            id: g.event.id,
            title: g.event.title,
          }))}
          availableChildren={children.map((c) => ({
            name: c.name,
            emoji_avatar: c.emoji_avatar,
            emoji_color: c.emoji_color,
            avatar_image_path: c.avatar_image_path,
          }))}
          availableMembers={availableMembers}
          initialMemberIds={
            formModal.mode === 'edit' && formModal.editItem
              ? (itemMemberIds.get(formModal.editItem.id) ?? [])
              : []
          }
          disambiguation={disambiguation}
          onSubmit={async (data) => {
            // review_items has no member column — the dropdown's picks
            // live in `itemMemberIds` and ride along on the approve
            // RPC. Strip the field before writing the review_item row
            // so PostgREST doesn't 400 on an unknown column.
            const { member_user_ids: pickedMembers, ...rest } = data;
            // ItemFormModal emits a NAIVE `${date}T${time}`; review_items
            // datetime columns are timestamptz, so a naive value is read back
            // as UTC and the saved hour/day shifts (YAR-271). Store an
            // offset-aware ISO in the user's timezone. toIsoWithTz is
            // idempotent (leaves already-offset values untouched).
            const withTz = (v: string | null) =>
              v ? toIsoWithTz(v, userTimezone) : v;
            const reviewData = {
              ...rest,
              start_datetime: withTz(rest.start_datetime),
              end_datetime: withTz(rest.end_datetime),
              due_datetime: withTz(rest.due_datetime),
            };
            if (formModal.mode === 'create') {
              const created = await emailService.createReviewItem({
                email_id: emailId,
                household_id: householdId!,
                ...reviewData,
              });
              setItemMemberIds((prev) => {
                if (pickedMembers.length === 0) return prev;
                const next = new Map(prev);
                next.set(created.id, pickedMembers);
                return next;
              });
            } else {
              await emailService.updateReviewItem(
                formModal.editItem!.id,
                householdId!,
                reviewData,
                // A type change drops any update/cancel proposal (same write).
                formModal.editItem!.type,
              );
              setItemMemberIds((prev) => {
                const next = new Map(prev);
                if (pickedMembers.length === 0) {
                  next.delete(formModal.editItem!.id);
                } else {
                  next.set(formModal.editItem!.id, pickedMembers);
                }
                return next;
              });
            }
            setFormModal(null);
            fetchData();
          }}
          onDelete={
            formModal.mode === 'edit' && formModal.editItem
              ? async () => {
                  const deletedId = formModal.editItem!.id;
                  const { emailDisposition } =
                    await emailService.deleteReviewItem(
                      deletedId,
                      householdId!,
                    );
                  // That was the email's last pending item: the server has
                  // closed the email (PROCESSED), so nothing more can be
                  // approved here — an item added now would be stranded.
                  // Leave through the same path as a discard: refresh the
                  // inbox list, then close.
                  if (emailDisposition !== null) {
                    setFormModal(null);
                    onDismissed?.();
                    onClose();
                    return;
                  }
                  // Drop the member-assignment entry too — leaving it
                  // behind would just stay in memory forever (the edge
                  // function already ignores keys not in the pending
                  // review_items set, so this is hygiene, not safety).
                  setItemMemberIds((prev) => {
                    if (!prev.has(deletedId)) return prev;
                    const next = new Map(prev);
                    next.delete(deletedId);
                    return next;
                  });
                  setFormModal(null);
                  fetchData();
                }
              : undefined
          }
          onBack={() => setFormModal(null)}
        />
      )}

      {/* Discard Confirmation Modal — YAR-140 AC3. The shared modal carries
          the Figma tones QA checked (icon #5e5e5e, title #1a1a1a, error
          #a82222); this screen only swaps in the copy that mentions items. */}
      <DiscardEmailModal
        open={discardModalOpen}
        onConfirm={() => void handleDismiss()}
        onCancel={() => setDiscardModalOpen(false)}
        loading={actionLoading}
        body={REVIEW_ITEMS_DISCARD_BODY}
      />

      {/* Confirm-without-a-date Modal (Path B — undated tasks) */}
      <ConfirmWithoutDateModal
        open={confirmWithoutDateOpen}
        tasks={
          confirmGate.kind === 'needs-task-confirmation'
            ? confirmGate.tasks.map((t) => ({ id: t.id, title: t.title }))
            : []
        }
        onAddDates={() => setConfirmWithoutDateOpen(false)}
        // Close synchronously, then approve: the footer button's spinner
        // covers the in-flight state, and closing first avoids a flash of an
        // empty task list while fetchData refreshes the now-committed items
        // behind the modal.
        onConfirmWithoutDate={() => {
          setConfirmWithoutDateOpen(false);
          handleApprove();
        }}
        onClose={() => setConfirmWithoutDateOpen(false)}
      />
    </>,
    document.body,
  );
}
